import { InfoSectionData } from './InfoButton';

export const productOperationsInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      <strong>Product Operations</strong> - management of operation templates (missions) that can be sold to fields.
      Each product operation defines the game scenario, required devices, objectives, and scoring rules.
      These templates are linked to products in the store catalog.
    `,
  },
  {
    title: 'Fields Description',
    fields: [
      {
        name: 'Title',
        description: 'The name of the operation/mission that will be displayed to customers.',
      },
      {
        name: 'Description',
        description: 'Detailed description of the mission scenario, rules, and gameplay.',
      },
      {
        name: 'Duration',
        description: 'Default duration of the operation in minutes.',
      },
      {
        name: 'Win Points / Lose Points',
        description: 'Points awarded to the winning and losing teams.',
      },
      {
        name: 'Price',
        description: 'Cost of the operation for field owners.',
      },
      {
        name: 'Is Active',
        description: 'Whether the operation is available for purchase.',
      },
    ],
  },
];

export const deviceCatalogInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      <strong>Device Catalog</strong> - a master library of all device types available in the NovaWarfare system.
      Each entry defines a specific device model with its capabilities, commands, and visual icon.
      <br/><br/>
      <strong>Purpose:</strong><br/>
      • Serves as the source for creating Field Mission Devices<br/>
      • Defines what commands each device type can execute<br/>
      • Provides icons displayed in the map editor
    `,
  },
  {
    title: 'Device Types',
    description: `
      <strong>Bomb</strong> - Devices for bomb planting/defusing scenarios<br/>
      <strong>Flag</strong> - Capture-the-flag objectives<br/>
      <strong>Beacon</strong> - Checkpoint or respawn points<br/>
      <strong>Control Point</strong> - Territory control objectives<br/>
      <strong>Target</strong> - Shooting targets with hit detection
    `,
  },
  {
    title: 'Fields Description',
    fields: [
      {
        name: 'Device Type',
        description: 'Category of the device (Bomb, Flag, Beacon, Control Point, etc.). Determines gameplay behavior.',
      },
      {
        name: 'Model',
        description: 'Hardware model identifier. Different models may have different features or firmware.',
      },
      {
        name: 'Description',
        description: 'Detailed description of the device, its purpose in gameplay, and any special features.',
      },
      {
        name: 'Icon URL',
        description: 'URL to the device icon image. This icon is displayed on map canvases when placing devices in Mission Templates.',
      },
      {
        name: 'Commands',
        description: 'List of commands the device can execute:<br/>' +
          '• <strong>Code</strong>: Unique command identifier sent to hardware<br/>' +
          '• <strong>Name</strong>: Human-readable command name<br/>' +
          '• <strong>Description</strong>: What the command does in gameplay<br/>' +
          '• <strong>Points Value</strong>: Points awarded when executed',
      },
      {
        name: 'Is Active',
        description: 'Whether the device type is available for creating new Field Mission Devices and operations.',
      },
    ],
  },
];

export const operationRecordsInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      <strong>Operation Records</strong> - complete mission configurations that can be sold to airsoft fields.
      Each record combines a product template with specific devices and team objectives.
      <br/><br/>
      <strong>Key Concept:</strong><br/>
      Operation Records are the "recipes" for missions. They define which devices are needed and what each team must accomplish to win.
    `,
  },
  {
    title: 'Workflow',
    description: `
      1. <strong>Select Product</strong> - Choose a base operation template (defines name, duration, points)<br/>
      2. <strong>Add Devices</strong> - Select which device types from the catalog are required<br/>
      3. <strong>Name Devices</strong> - Give each device a game-specific name (e.g., "Alpha Bomb", "Blue Flag")<br/>
      4. <strong>Configure Objectives</strong> - Define goals for each team with their target devices<br/>
      5. <strong>Save</strong> - Record gets a unique 10-digit code for selling to fields
    `,
  },
  {
    title: 'Fields Description',
    fields: [
      {
        name: 'Unique Code',
        description: 'Auto-generated 10-digit code that uniquely identifies this operation. Fields use this code when purchasing.',
      },
      {
        name: 'Product',
        description: 'The base operation template. Determines the mission name, default duration, win/lose points, and price.',
      },
      {
        name: 'Devices',
        description: 'Required devices for this operation:<br/>' +
          '• <strong>Device from Catalog</strong>: The device type (Bomb, Flag, etc.)<br/>' +
          '• <strong>Device Name</strong>: In-game name shown to players (e.g., "Bomb A")<br/>' +
          '• <strong>Is Critical</strong>: If true, mission cannot start without this device being assigned',
      },
      {
        name: 'Objectives',
        description: 'Team goals that determine victory conditions:<br/>' +
          '• <strong>Name</strong>: Objective title shown in-game<br/>' +
          '• <strong>Type</strong>: Objective category (plant bomb, capture flag, hold point, etc.)<br/>' +
          '• <strong>Device</strong>: Which device this objective uses<br/>' +
          '• <strong>Command</strong>: Which device command triggers objective completion<br/>' +
          '• <strong>Team</strong>: Which team has this objective (Red/Blue)<br/>' +
          '• <strong>Is Critical</strong>: If true, completing this objective wins the game',
      },
    ],
  },
];

export const fieldConfigsInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      <strong>Field Configs</strong> - represents sold operations linked to specific airsoft fields.
      This is the final step where an Operation Record is assigned to a field with their physical devices.
      <br/><br/>
      <strong>Important:</strong><br/>
      After creating a Field Config, the field can use this operation in their games and create Mission Templates in the map editor.
    `,
  },
  {
    title: 'Workflow',
    description: `
      1. <strong>Select Field</strong> - Choose a registered airsoft field<br/>
      2. <strong>Select Operation</strong> - Choose the Operation Record to sell<br/>
      3. <strong>Map Devices</strong> - Link each operation device to a physical Field Mission Device from the field's inventory<br/>
      4. <strong>Set Activation</strong> - Optionally set when the operation becomes available<br/>
      5. <strong>Save</strong> - The field can now use this operation
    `,
  },
  {
    title: 'Fields Description',
    fields: [
      {
        name: 'Sale Number',
        description: 'Auto-generated unique identifier for this sale in format: NW-YYYY-MM-DD-XXXX.',
      },
      {
        name: 'Field',
        description: 'The registered airsoft field receiving this operation. Only verified fields appear in the dropdown.',
      },
      {
        name: 'Operation',
        description: 'The Operation Record being sold. Shows the operation name and unique code.',
      },
      {
        name: 'Device Mapping',
        description: 'Links each operation device requirement to a physical Field Mission Device:<br/>' +
          '• <strong>Operation Device</strong>: Required device from the Operation Record (e.g., "Bomb A")<br/>' +
          '• <strong>Field Device</strong>: Physical device from the field\'s inventory (matched by device type)',
      },
      {
        name: 'Activation Date',
        description: 'Optional date when this operation becomes available for the field. Leave empty for immediate activation.',
      },
      {
        name: 'Status',
        description: 'Active/Inactive - controls whether the field can currently use this operation in games.',
      },
    ],
  },
];

export const fieldNodesInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      <strong>Field Nodes</strong> - WiFi network devices that have been registered in the system.
      These are the actual hardware devices that enable communication between the game server and game devices on the field.
      Nodes can be sold to fields and assigned to maps for gameplay.
    `,
  },
  {
    title: 'Node Lifecycle',
    description: `
      1. <strong>Create</strong>: Register a new node with Node Code. Sale Number is auto-generated.<br/>
      2. <strong>Sell</strong>: Mark the node as sold to a specific field. Once sold, the node cannot be re-sold.<br/>
      3. <strong>Assign to Map</strong>: On the field side, use Node Code to bind the node to a map.
    `,
  },
  {
    title: 'Node Types',
    description: `
      <strong>InternetNode (Gateway)</strong> - Main node that connects to the internet. One per field, used as the office/main node.<br/>
      <strong>MapNode (Device)</strong> - Regular game device placed on the map for objectives and tracking.
    `,
  },
  {
    title: 'Fields Description',
    fields: [
      {
        name: 'Node Code',
        description: 'Unique identifier for map binding. Used by field owners to find and assign nodes to maps.',
      },
      {
        name: 'Sale Number',
        description: 'Auto-generated sale identifier in format: NW-{initials}-YYYY-MM-DD-{number}-{letters}. Generated when node is created.',
      },
      {
        name: 'Node Type',
        description: 'Type of the node: InternetNode (gateway) or MapNode (game device).',
      },
      {
        name: 'Friendly Name',
        description: 'Human-readable name for easy identification.',
      },
      {
        name: 'Field',
        description: 'The field this node is currently assigned to (can be changed after sale).',
      },
      {
        name: 'Status (SOLD/NOT SOLD)',
        description: 'Indicates whether the node has been sold. Once sold, this status is permanent and cannot be reverted.',
      },
    ],
  },
];

export const fieldMissionDeviceInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      <strong>Field Mission Devices</strong> - physical game devices assigned to specific airsoft fields.
      These are the actual hardware units (bombs, flags, control points) that fields use during games.
      <br/><br/>
      <strong>Purpose:</strong><br/>
      • Created from the Device Catalog templates<br/>
      • Assigned to a specific field's inventory<br/>
      • Linked to operations via Field Configs<br/>
      • Displayed on map canvases in Mission Templates
    `,
  },
  {
    title: 'Workflow',
    description: `
      1. <strong>Select Device Type</strong> - Choose from the Device Catalog (Bomb, Flag, etc.)<br/>
      2. <strong>Enter Device Code</strong> - The unique code printed on the physical hardware<br/>
      3. <strong>Assign to Field</strong> - Select which airsoft field owns this device<br/>
      4. <strong>Save</strong> - Device is now in the field's inventory and can be used in Field Configs
    `,
  },
  {
    title: 'Usage in Map Editor',
    description: `
      When a field creates <strong>Mission Templates</strong> in the map editor, they can position their Field Mission Devices on the canvas.
      <br/><br/>
      • Device icon from the Device Catalog is displayed<br/>
      • Device name and type shown as labels<br/>
      • Positions are saved per-operation template<br/>
      • Referees can see where to place each device before a game starts
    `,
  },
  {
    title: 'Fields Description',
    fields: [
      {
        name: 'Device Code',
        description: 'Unique identifier printed on the physical device hardware. Used to link the digital record to the physical unit.',
      },
      {
        name: 'Device Type',
        description: 'The category of device (Bomb, Flag, Beacon, Control Point, Target). Determines what operations can use this device.',
      },
      {
        name: 'Model',
        description: 'The specific hardware model from the Device Catalog. Different models may have different features.',
      },
      {
        name: 'Field',
        description: 'The airsoft field that owns this physical device. Only devices assigned to a field can be used in that field\'s operations.',
      },
    ],
  },
];
